import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage, defineMessages} from 'react-intl';
import {connect} from 'react-redux';

import check from './check.svg';
import dropdownCaret from './dropdown-caret.svg';
import {MenuItem, Submenu} from '../menu/menu.jsx';
import {ACCENT_BLUE, ACCENT_MAP, ACCENT_PURPLE, ACCENT_RED, ACCENT_RAINBOW, ACCENT_FE, ACCENT_COSTOM, ACCENT_CE, ACCENT_TY, ACCENT_MIKU, Theme} from '../../lib/themes/index.js';
import {openAccentMenu, accentMenuOpen, closeSettingsMenu} from '../../reducers/menus.js';
import {setTheme} from '../../reducers/theme.js';
import {persistTheme} from '../../lib/themes/themePersistance.js';
import rainbowIcon from './tw-accent-rainbow.svg';
import styles from './settings-menu.css';

const options = defineMessages({
    [ACCENT_RED]: {
        defaultMessage: 'Red',
        description: 'Name of the red color scheme, used by FrostEditor by default.',
        id: 'tw.accent.red'
    },
    [ACCENT_PURPLE]: {
        defaultMessage: 'Purple',
        description: 'Name of the purple color scheme. Matches modern Scratch.',
        id: 'tw.accent.purple'
    },
    [ACCENT_BLUE]: {
        defaultMessage: 'Blue',
        description: 'Name of the blue color scheme. Matches Scratch before the high contrast update.',
        id: 'tw.accent.blue'
    },
    [ACCENT_RAINBOW]: {
        defaultMessage: 'Rainbow',
        description: 'Name of color scheme that uses a rainbow.',
        id: 'tw.accent.rainbow'
    },
    [ACCENT_FE]: {
        defaultMessage: 'FrostEditor',
        description: 'Name of the FrostEditor color scheme.',
        id: 'tw.accent.frosteditor'
    },
    [ACCENT_COSTOM]: {
        defaultMessage: 'Custom',
        description: 'Name of the custom color scheme.',
        id: 'tw.accent.custom'
    },
    [ACCENT_CE]: {
        defaultMessage: 'CE',
        description: 'Name of the CE color scheme.',
        id: 'tw.accent.ce'
    },
    [ACCENT_TY]: {
        defaultMessage: 'TY',
        description: 'Name of the TY color scheme.',
        id: 'tw.accent.ty'
    },
    [ACCENT_MIKU]: {
        defaultMessage: 'Miku',
        description: 'Name of the Miku color scheme.',
        id: 'tw.accent.miku'
    }
});

const icons = {
    [ACCENT_RAINBOW]: rainbowIcon
};

const ColorIcon = props => (
    icons[props.id] ? (
        <img
            className={styles.accentIconOuter}
            src={icons[props.id]}
            draggable={false}
            // Image is decorative
            alt=""
        />
    ) : (
        <div
            className={styles.accentIconOuter}
            style={{
                // menu-bar-background is var(...), don't want to evaluate with the current values
                backgroundColor: ACCENT_MAP[props.id].guiColors['looks-secondary'],
                backgroundImage: ACCENT_MAP[props.id].guiColors['menu-bar-background-image']
            }}
        />
    )
);

ColorIcon.propTypes = {
    id: PropTypes.string
};

const AccentMenuItem = props => (
    <MenuItem onClick={props.onClick}>
        <div className={styles.option}>
            <img
                className={classNames(styles.check, {[styles.selected]: props.isSelected})}
                width={15}
                height={12}
                src={check}
                draggable={false}
            />
            <ColorIcon id={props.id} />
            <FormattedMessage {...options[props.id]} />
        </div>
    </MenuItem>
);

AccentMenuItem.propTypes = {
    id: PropTypes.string,
    isSelected: PropTypes.bool,
    onClick: PropTypes.func
};

const AccentThemeMenu = ({
    isOpen,
    isRtl,
    onChangeTheme,
    onOpen,
    theme
}) => (
    <MenuItem expanded={isOpen}>
        <div
            className={styles.option}
            onClick={onOpen}
        >
            <ColorIcon id={theme.accent} />
            <span className={styles.submenuLabel}>
                <FormattedMessage
                    defaultMessage="Accent"
                    description="Label for menu to choose accent color (eg. FrostEditor's red, Scratch's purple)"
                    id="tw.menuBar.accent"
                />
            </span>
            <img
                className={styles.expandCaret}
                src={dropdownCaret}
                draggable={false}
            />
        </div>
        <Submenu place={isRtl ? 'left' : 'right'}>
            {/* Accent color options */}
            <AccentMenuItem
                id={ACCENT_FE}
                isSelected={theme.accent === ACCENT_FE}
                onClick={() => onChangeTheme(theme.set('accent', ACCENT_FE))}
            />
            <AccentMenuItem
                id={ACCENT_RED}
                isSelected={theme.accent === ACCENT_RED}
                onClick={() => onChangeTheme(theme.set('accent', ACCENT_RED))}
            />
            <AccentMenuItem
                id={ACCENT_PURPLE}
                isSelected={theme.accent === ACCENT_PURPLE}
                onClick={() => onChangeTheme(theme.set('accent', ACCENT_PURPLE))}
            />
            <AccentMenuItem
                id={ACCENT_BLUE}
                isSelected={theme.accent === ACCENT_BLUE}
                onClick={() => onChangeTheme(theme.set('accent', ACCENT_BLUE))}
            />
            <AccentMenuItem
                id={ACCENT_RAINBOW}
                isSelected={theme.accent === ACCENT_RAINBOW}
                onClick={() => onChangeTheme(theme.set('accent', ACCENT_RAINBOW))}
            />
            <AccentMenuItem
                id={ACCENT_COSTOM}
                isSelected={theme.accent === ACCENT_COSTOM}
                onClick={() => onChangeTheme(theme.set('accent', ACCENT_COSTOM))}
            />
            <AccentMenuItem
                id={ACCENT_CE}
                isSelected={theme.accent === ACCENT_CE}
                onClick={() => onChangeTheme(theme.set('accent', ACCENT_CE))}
            />
            <AccentMenuItem
                id={ACCENT_TY}
                isSelected={theme.accent === ACCENT_TY}
                onClick={() => onChangeTheme(theme.set('accent', ACCENT_TY))}
            />
            <AccentMenuItem
                id={ACCENT_MIKU}
                isSelected={theme.accent === ACCENT_MIKU}
                onClick={() => onChangeTheme(theme.set('accent', ACCENT_MIKU))}
            />
        </Submenu>
    </MenuItem>
);

AccentThemeMenu.propTypes = {
    isOpen: PropTypes.bool,
    isRtl: PropTypes.bool,
    onChangeTheme: PropTypes.func,
    onOpen: PropTypes.func,
    theme: PropTypes.instanceOf(Theme)
};

const mapStateToProps = state => ({
    isOpen: accentMenuOpen(state),
    isRtl: state.locales.isRtl,
    theme: state.scratchGui.theme.theme
});

const mapDispatchToProps = dispatch => ({
    onChangeTheme: theme => {
        dispatch(setTheme(theme));
        dispatch(closeSettingsMenu());
        persistTheme(theme);
    },
    onOpen: () => dispatch(openAccentMenu())
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(AccentThemeMenu);
